import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { apiRequest } from '../api/client'
import type {
    AdminEmailLogsPage,
    AdminSettings,
    AdminStats,
    AdminUsersPage,
    EmailLogStatus,
    EmailLogType,
    PageviewRow,
    SendFreeMailBody,
    SendTemplateMailBody,
} from '../types/api'

const USER_PAGE_LIMIT = 50
const EMAIL_LOG_PAGE_LIMIT = 50

/** Filter-Werte des E-Mail-Log-Panels ('ALL' = kein Filter). */
export type EmailLogStatusFilter = EmailLogStatus | 'ALL'
export type EmailLogTypeFilter = EmailLogType | 'ALL'

export function useAdminStats() {
    return useQuery({
        queryKey: ['admin-stats'],
        queryFn: () => apiRequest<AdminStats>('/admin/stats'),
    })
}

/** Serverseitige Suche + „Mehr laden" über useInfiniteQuery (limit 50). */
export function useAdminUsers(search: string) {
    return useInfiniteQuery({
        queryKey: ['admin-users', search],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: String(USER_PAGE_LIMIT),
            })
            if (search) params.set('search', search)
            return apiRequest<AdminUsersPage>(`/admin/users?${params}`)
        },
        getNextPageParam: lastPage =>
            lastPage.meta.page < lastPage.meta.totalPages
                ? lastPage.meta.page + 1
                : undefined,
    })
}

export function useAdminPageviews() {
    return useQuery({
        queryKey: ['admin-pageviews'],
        queryFn: () => apiRequest<PageviewRow[]>('/admin/pageviews'),
    })
}

/** E-Mail-Log: Suche/Filter serverseitig + „Mehr laden" wie UsersPanel. */
export function useAdminEmailLogs(
    search: string,
    status: EmailLogStatusFilter,
    mailType: EmailLogTypeFilter,
) {
    return useInfiniteQuery({
        queryKey: ['admin-email-logs', search, status, mailType],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: String(EMAIL_LOG_PAGE_LIMIT),
            })
            if (search) params.set('search', search)
            if (status !== 'ALL') params.set('status', status)
            if (mailType !== 'ALL') params.set('mailType', mailType)
            return apiRequest<AdminEmailLogsPage>(`/admin/email-logs?${params}`)
        },
        getNextPageParam: lastPage =>
            lastPage.meta.page < lastPage.meta.totalPages
                ? lastPage.meta.page + 1
                : undefined,
    })
}

export function useAdminSettings() {
    return useQuery({
        queryKey: ['admin-settings'],
        queryFn: () => apiRequest<AdminSettings>('/admin/settings'),
    })
}

/** Admin: PUT /admin/settings/signup — Registrierung zur Laufzeit freischalten/sperren. */
export function useSetSignupEnabled() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (enabled: boolean) =>
            apiRequest<AdminSettings>('/admin/settings/signup', {
                method: 'PUT',
                body: { enabled },
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin-settings'] })
        },
    })
}

/** Admin: POST /admin/test-mail — Mail-Versandweg prüfen (204). */
export function useSendTestMail() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (to: string) =>
            apiRequest<void>('/admin/test-mail', {
                method: 'POST',
                body: { to },
            }),
        onSuccess: () => {
            // Versand (auch Fehlversand) landet im E-Mail-Log → frisch laden
            void qc.invalidateQueries({ queryKey: ['admin-email-logs'] })
        },
    })
}

/** Admin: POST /admin/template-mail — Vorlagen-Preview in de/en (204). */
export function useSendTemplateMail() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: SendTemplateMailBody) =>
            apiRequest<void>('/admin/template-mail', {
                method: 'POST',
                body,
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin-email-logs'] })
        },
    })
}

/** Admin: POST /admin/free-mail — Freitext-Mail mit freiem Betreff (204). */
export function useSendFreeMail() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: SendFreeMailBody) =>
            apiRequest<void>('/admin/free-mail', {
                method: 'POST',
                body,
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin-email-logs'] })
        },
    })
}
